<template>
      <div class="ticket-nutrizionali-container">
        <div class="titolo-card">
          <div class="header-row">
            <h1>Ticket Nutrizionali</h1>
            <button class="create-ticket-button" @click="openCreateTicketModal">
              Nuovo Ticket
            </button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Titolo</th>
              <th>Status</th>
              <th>Azioni</th> <!-- Per azioni come "vedi conversazione" -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in tickets" :key="ticket.id">
              <td>{{ formatDate(ticket.timestamp) }}</td>
              <td>{{ ticket.title }}</td>
              <td>{{ ticket.status }}</td>
              <td>
                <button @click="viewConversation(ticket.id)">Vedi Conversazione</button>
              </td>
            </tr>
          </tbody>
        </table>

        <teleport to="body">
          <div v-if="showCreateTicketModal" class="modal-overlay" @click.self="closeCreateTicketModal">
            <div class="modal-content">
              <span class="close" @click="closeCreateTicketModal">&times;</span>
              <h2>Nuovo ticket nutrizionale</h2>
              <form @submit.prevent="createTicket">
                <label for="ticket-title">Titolo</label>
                <input id="ticket-title" v-model="newTicket.title" type="text" required />

                <label for="ticket-message">Messaggio</label>
                <textarea id="ticket-message" v-model="newTicket.message" rows="6" required></textarea>

                <div class="modal-actions">
                  <button type="button" class="secondary-button staff-button-cancel" @click="closeCreateTicketModal">Annulla</button>
                  <button class="staff-button-positive" type="submit" :disabled="isCreatingTicket">
                    {{ isCreatingTicket ? 'Invio...' : 'Crea Ticket' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </teleport>
      </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getFirestore, collection, query, where, orderBy, onSnapshot, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
export default {
  name: "UsersTicketNutrizionali",
  setup() {
    const tickets = ref([]);
    const userId = ref(null);
    const showCreateTicketModal = ref(false);
    const isCreatingTicket = ref(false);
    const newTicket = ref({
      title: '',
      message: '',
    });
    const route = useRoute();
    const router = useRouter();
    const db = getFirestore();
    const functions = getFunctions();
    let unsubscribeTickets = null;

    const enableDocumentUploadByDefault = async () => {
      const userRef = doc(db, 'users', userId.value);
      const userSnapshot = await getDoc(userRef);
      if (!userSnapshot.exists()) return;

      if (typeof userSnapshot.data().canUploadNutritionalFiles !== 'boolean') {
        await updateDoc(userRef, { canUploadNutritionalFiles: true });
      }
    };

    onMounted(() => {
      userId.value = route.params.userId;
      fetchTickets();
    });

    onBeforeUnmount(() => {
      if (typeof unsubscribeTickets === 'function') {
        unsubscribeTickets();
        unsubscribeTickets = null;
      }
    });



    const formatDate = (timestamp) => {
  let date;

  // Se il timestamp è un oggetto Firestore Timestamp
  if (timestamp && typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  } else {
    // Se è una stringa ISO o un numero
    date = new Date(timestamp);
  }

  // Verifica che la data sia valida
  if (isNaN(date.getTime())) {
    return 'Data non valida'; // Restituisce un messaggio di errore se la data è invalida
  }

  return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
};



    // Funzione per recuperare i ticket nutrizionali per l'utente
    const fetchTickets = () => {
      if (!userId.value) {
        console.error("UserID non specificato");
        return;
      }

      const ticketQuery = query(
        collection(db, "tickets"),
        where("userId", "==", userId.value), // Filtra per userId
        orderBy("timestamp", "asc") // Ordina per data
      );

      if (typeof unsubscribeTickets === 'function') {
        unsubscribeTickets();
        unsubscribeTickets = null;
      }

      unsubscribeTickets = onSnapshot(ticketQuery, (querySnapshot) => {
        tickets.value = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }, (error) => {
        console.error('Errore durante il recupero dei ticket nutrizionali:', error);
        tickets.value = [];
      });
    };

    // Funzione per navigare alla conversazione di un ticket
    const viewConversation = (ticketId) => {
  router.push(`/assistenzanutrizionale/conversation/${ticketId}`);
};

    const openCreateTicketModal = () => {
      showCreateTicketModal.value = true;
    };

    const closeCreateTicketModal = () => {
      showCreateTicketModal.value = false;
      newTicket.value = {
        title: '',
        message: '',
      };
    };

    const sendNotification = async (uid) => {
      const sendNotificationFunction = httpsCallable(functions, 'sendNotificationAndEmail');
      await sendNotificationFunction({ userId: uid, notificationType: 'nutrizionale' });
    };

    const createTicket = async () => {
      if (isCreatingTicket.value) {
        return;
      }

      if (!userId.value) {
        alert('Utente non valido.');
        return;
      }

      const title = String(newTicket.value.title || '').trim();
      const message = String(newTicket.value.message || '').trim();
      if (!title || !message) {
        alert('Inserisci titolo e messaggio.');
        return;
      }

      isCreatingTicket.value = true;
      try {
        const auth = getAuth();
        const operatorId = auth.currentUser?.uid || null;
        const clientTimestamp = new Date().toISOString();
        const uniqueTicketId = `TICKET-${Math.random().toString(36).slice(2, 11)}`;

        const ticketData = {
          idTicket: uniqueTicketId,
          title,
          status: 'Non Risposto',
          userId: userId.value,
          operatorId,
          timestamp: clientTimestamp,
          conversations: [
            { type: 'Nutrizionista', content: message, timestamp: clientTimestamp, userId: operatorId }
          ]
        };

        const docRef = await addDoc(collection(db, 'tickets'), ticketData);
        await enableDocumentUploadByDefault();

        try {
          await sendNotification(userId.value);
        } catch (notificationError) {
          console.error('Ticket creato ma notifica non inviata:', notificationError);
          alert('Ticket creato, ma notifica non inviata.');
        }

        closeCreateTicketModal();
        viewConversation(docRef.id);
      } catch (error) {
        console.error('Errore nella creazione del ticket:', error);
        alert('Errore nella creazione del ticket.');
      } finally {
        isCreatingTicket.value = false;
      }
    };


    return {
      tickets,
      formatDate,
      viewConversation,
      userId,
      showCreateTicketModal,
      openCreateTicketModal,
      closeCreateTicketModal,
      newTicket,
      createTicket,
      isCreatingTicket,
    };
  }
};
</script>

<style scoped>



#data {
  margin-bottom: 10px; /* Margine aggiunto */
}


.material-icons{
  margin-left: 5px;
}
table {
  width: 98%;
  padding: 10px;
}
th, td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

th:nth-child(2), th:nth-child(3) { /* Data, Tipo, Sede */
  width: 10%;
}
th:nth-child(4) { /* Note */
  width: 20%;
}

th:nth-child(1) { /* Data, Tipo, Sede */
  width: 4%;
}

tbody tr:nth-child(odd) {
  background-color: #f5f5f5; /* Grigio chiaro per le righe dispari */
}

tbody tr:nth-child(even) {
  background-color: #e9e9e9; /* Grigio scuro per le righe pari */
}


/* Stili per il componente dei ticket nutrizionali */
.ticket-nutrizionali-container {
  margin-top: 20px;
}

.header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.create-ticket-button {
  background-color: #1f6feb;
}

.create-ticket-button:hover {
  background-color: #1a5dc8;
}



button {
  padding: 13px 13px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
}

button:hover {
  background-color: #45a049;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.56);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 24px;
  box-sizing: border-box;
}

.modal-content {
  width: min(430px, calc(100vw - 48px));
  padding: 28px;
  border-radius: 8px;
  margin-left: 0;
  position: relative;
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.28);
  font-family: "Montserrat", Avenir, Helvetica, Arial, sans-serif;
}

.modal-content h2 {
  margin: 0 0 22px;
  color: #0f172a;
  font-family: inherit;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
}

.modal-content form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-content input,
.modal-content textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(148, 163, 184, 0.6);
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.4;
  outline: none;
}

.modal-content input {
  min-height: 44px;
  padding: 10px 12px;
}

.modal-content textarea {
  min-height: 150px;
  max-height: 260px;
  padding: 12px;
  resize: vertical;
}

.modal-content input:focus,
.modal-content textarea:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
}

.modal-actions {
  display: flex;
  gap: 10px;
  margin-top: 14px;
}

.modal-content label {
  display: block;
  margin-bottom: 2px;
  color: #334155;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 800;
}

.close {
  position: absolute;
  top: 14px;
  right: 16px;
  cursor: pointer;
  color: #64748b;
  font-family: inherit;
  font-size: 22px;
  line-height: 1;
}

.close:hover {
  color: #0f172a;
}

.secondary-button {
  background-color: #e2e8f0;
  color: #0f172a;
}

.secondary-button:hover {
  background-color: #cbd5e1;
}
</style>
